import { Controller } from "@hotwired/stimulus"
import { formatValue } from "../lib/number_format"
import { track } from "../lib/umami"
import {
  bookPercentage,
  decimalToProbability,
  normalizeProbabilities,
  oddsWarning,
  overround,
  parseDecimalOdds,
  theoreticalMargin,
  theoreticalPayout
} from "../lib/odds_math"

// Calculateur de marge bookmaker autonome (/calculateur-marge-bookmaker, #1836).
//
// Une ligne par issue, ajoutée ou supprimée à la volée. Le gabarit d'une nouvelle
// ligne vient d'un <template> rendu par le même partial Rails que les lignes
// initiales : aucune classe ni structure n'est réécrite en JavaScript.
//
// Le retrait de marge appliqué est la normalisation proportionnelle, exposée telle
// quelle par le moteur (odds_math.normalizeProbabilities).
//
// Note sécurité : le seul HTML inséré ici est le contenu du <template> rendu par
// Rails (markup statique, échappé à la génération). Les valeurs saisies par
// l'utilisateur ne transitent jamais par insertAdjacentHTML — elles sont posées via
// la propriété `value` d'un input, et les résultats via `textContent`.
export default class extends Controller {
  static targets = [
    "rows", "row", "odds", "outcomeLabel", "removeButton", "addButton",
    "template", "results", "warning", "warningText", "copyButton"
  ]
  static values = {
    locale: { type: String, default: "fr" },
    messages: { type: Object, default: {} },
    presets: { type: Object, default: {} },
    minOutcomes: { type: Number, default: 2 },
    maxOutcomes: { type: Number, default: 12 }
  }

  connect() {
    this.defaults = this.rowTargets.map((row) => ({
      label: this.labelInput(row)?.value ?? "",
      odds: this.oddsInput(row)?.value ?? ""
    }))
    this.copyLabel = this.hasCopyButtonTarget ? this.copyButtonTarget.textContent : null
    this.compute()
  }

  compute(event) {
    this.trackUse(event)

    const rows = this.rowTargets
    const rawOdds = rows.map((row) => this.oddsInput(row)?.value)
    const decimals = rawOdds.map((value) => parseDecimalOdds(value))

    this.renderWarning(this.firstWarning(rawOdds))
    this.renderRows(rows, decimals)
    this.renderMarket(decimals.filter((value) => value !== null && value > 1))
    this.syncButtons()
  }

  addOutcome() {
    if (this.rowTargets.length >= this.maxOutcomesValue) {
      this.renderWarning("max_outcomes")
      return
    }

    const index = this.rowTargets.length
    const markup = this.templateTarget.innerHTML.replaceAll("MARGIN_ROW_INDEX", String(index))
    this.rowsTarget.insertAdjacentHTML("beforeend", markup)
    track("marginCalculatorOutcome_added")
    this.compute()
    this.oddsInput(this.rowTargets[index])?.focus()
  }

  removeOutcome(event) {
    if (this.rowTargets.length <= this.minOutcomesValue) return

    event.currentTarget.closest(".calc-row").remove()
    this.reindex()
    this.compute()
  }

  applyPreset(event) {
    const outcomes = this.presetsValue[event.currentTarget.dataset.preset]
    if (!outcomes) return

    track("marginCalculatorPreset_applied", { preset: event.currentTarget.dataset.preset })

    this.rowsTarget.innerHTML = ""
    outcomes.forEach((outcome, index) => {
      this.rowsTarget.insertAdjacentHTML(
        "beforeend",
        this.templateTarget.innerHTML.replaceAll("MARGIN_ROW_INDEX", String(index))
      )
      const row = this.rowTargets[index]
      this.labelInput(row).value = outcome.label
      this.oddsInput(row).value = outcome.odds
    })
    this.compute()
  }

  reset() {
    track("marginCalculator_reset")
    this.rowsTarget.innerHTML = ""
    this.defaults.forEach((outcome, index) => {
      this.rowsTarget.insertAdjacentHTML(
        "beforeend",
        this.templateTarget.innerHTML.replaceAll("MARGIN_ROW_INDEX", String(index))
      )
      const row = this.rowTargets[index]
      this.labelInput(row).value = outcome.label
      this.oddsInput(row).value = outcome.odds
    })
    this.compute()
  }

  async copy() {
    if (!this.hasCopyButtonTarget) return

    try {
      await navigator.clipboard.writeText(this.summary())
      track("marginCalculatorResult_copied")
      this.copyButtonTarget.textContent = this.messagesValue.copied || this.copyLabel
      window.setTimeout(() => { this.copyButtonTarget.textContent = this.copyLabel }, 2000)
    } catch {
      // Presse-papiers indisponible : pas de confirmation trompeuse.
    }
  }

  preventSubmit(event) {
    event.preventDefault()
    this.compute()
  }

  // -------------------------------------------------------------------------

  // Une seule fois par visite, au premier changement saisi par l'utilisateur : le
  // compute() du connect() ne doit pas compter comme un usage.
  trackUse(event) {
    if (this.used || event?.type !== "input") return

    this.used = true
    track("marginCalculator_used")
  }

  labelInput(row) { return row.querySelector("[data-margin-calculator-target='outcomeLabel']") }
  oddsInput(row) { return row.querySelector("[data-margin-calculator-target='odds']") }

  // Les noms de champs restent contigus après suppression d'une ligne : sans ce
  // renumérotage, une soumission sans JavaScript enverrait des index à trous.
  reindex() {
    this.rowTargets.forEach((row, index) => {
      row.querySelectorAll("input, label").forEach((node) => {
        [ "name", "id", "for" ].forEach((attribute) => {
          const value = node.getAttribute(attribute)
          if (value) node.setAttribute(attribute, value.replace(/\d+/, String(index)))
        })
      })
    })
  }

  firstWarning(rawOdds) {
    for (const value of rawOdds) {
      if (String(value ?? "").trim() === "") continue

      const code = oddsWarning(value)
      if (code) return code
    }
    return null
  }

  renderRows(rows, decimals) {
    const valid = decimals.filter((value) => value !== null && value > 1)
    const normalized = normalizeProbabilities(valid.map((value) => decimalToProbability(value)))
    let cursor = 0

    rows.forEach((row, index) => {
      const decimal = decimals[index]
      const usable = decimal !== null && decimal > 1
      const share = usable ? normalized[cursor++] : null

      this.renderOutputs(row, {
        implied_probability: usable ? decimalToProbability(decimal) * 100 : null,
        normalized_probability: share === undefined || share === null ? null : share * 100,
        fair_odds: share === undefined || share === null ? null : 1 / share
      })
    })
  }

  renderMarket(decimals) {
    const book = bookPercentage(decimals)
    const values = book === null ? {} : {
      book_percentage: book * 100,
      overround: overround(decimals) * 100,
      theoretical_payout: theoreticalPayout(decimals) * 100,
      theoretical_margin: theoreticalMargin(decimals) * 100
    }

    if (this.hasResultsTarget) this.renderOutputs(this.resultsTarget, values)
  }

  renderOutputs(scope, values) {
    scope.querySelectorAll("[data-output]").forEach((output) => {
      const value = values[output.dataset.output]
      output.textContent = value === null || value === undefined
        ? "—"
        : formatValue(value, output.dataset.format, this.localeValue)
    })
  }

  // Le bouton d'ajout et les boutons de suppression reflètent les bornes : un
  // marché descend rarement sous deux issues, et jamais au-dessus du plafond.
  syncButtons() {
    const count = this.rowTargets.length
    this.removeButtonTargets.forEach((button) => { button.disabled = count <= this.minOutcomesValue })
    if (this.hasAddButtonTarget) this.addButtonTarget.disabled = count >= this.maxOutcomesValue
  }

  renderWarning(code) {
    if (!this.hasWarningTarget) return

    if (!code) {
      this.warningTarget.hidden = true
      if (this.hasWarningTextTarget) this.warningTextTarget.textContent = ""
      return
    }

    this.warningTarget.hidden = false
    if (this.hasWarningTextTarget) this.warningTextTarget.textContent = this.messagesValue[code] || ""
  }

  summary() {
    const rows = this.rowTargets.map((row) => {
      const label = this.labelInput(row)?.value || ""
      const odds = this.oddsInput(row)?.value || ""
      const outputs = Array.from(row.querySelectorAll("[data-output]")).map((output) => output.textContent)
      return [ label, odds, ...outputs ].join(" · ")
    })

    const market = this.hasResultsTarget
      ? Array.from(this.resultsTarget.querySelectorAll(".calc-result")).map((result) =>
        `${result.querySelector("dt").textContent}: ${result.querySelector("dd").textContent}`)
      : []

    return [ ...rows, ...market ].join("\n")
  }
}
